import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { toast } from "sonner";
import { Loader2, Calendar, Save, Phone } from "lucide-react";
import { createCalendarEvent, updateCalendarEvent } from "../../services/crmServices/calendarService";
import { getCurrentTime } from "../../hooks/useTimeOffset";

/**
 * Reusable modal for scheduling or editing a callback / calendar event.
 *
 * Props:
 *   open               – boolean
 *   onClose            – fn
 *   onSaved            – fn(event)
 *   lead               – { _id, firstName, lastName, phone, email }  (optional, used to pre-fill)
 *   existingEvent      – calendar event to edit  (optional, switches to edit mode)
 *   defaultStartAt     – ISO string  (optional, used for quick-create from a calendar slot)
 */
export function ScheduleCallbackModal({ open, onClose, onSaved, lead, existingEvent, defaultStartAt }) {
    const isEdit = !!existingEvent?._id;

    // Compute a clean default start = next hour, rounded.
    // Uses the user's adjusted current time so wrong system clocks don't matter.
    const defaultStart = () => {
        if (defaultStartAt) return localInputValue(defaultStartAt);
        if (existingEvent?.startAt) return localInputValue(existingEvent.startAt);
        const d = getCurrentTime();
        d.setMinutes(0, 0, 0);
        d.setHours(d.getHours() + 1);
        return localInputValue(d);
    };

    const [form, setForm] = useState({
        title: existingEvent?.title || (lead ? `Callback: ${lead.firstName || ""} ${lead.lastName || ""}`.trim() : "Callback"),
        type: existingEvent?.type || "callback",
        priority: existingEvent?.priority || "normal",
        startAt: defaultStart(),
        durationMinutes: existingEvent?.durationMinutes || 15,
        reminderMinutesBefore: existingEvent?.reminderMinutesBefore ?? 10,
        notes: existingEvent?.notes || ""
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // Re-init the form when modal re-opens with a new context
        if (!open) return;
        setForm({
            title: existingEvent?.title || (lead ? `Callback: ${lead.firstName || ""} ${lead.lastName || ""}`.trim() : "Callback"),
            type: existingEvent?.type || "callback",
            priority: existingEvent?.priority || "normal",
            startAt: defaultStart(),
            durationMinutes: existingEvent?.durationMinutes || 15,
            reminderMinutesBefore: existingEvent?.reminderMinutesBefore ?? 10,
            notes: existingEvent?.notes || ""
        });
    }, [open, existingEvent, lead, defaultStartAt]);

    const handleSave = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        if (!form.startAt) {
            toast.error("Pick a date and time");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                type: form.type,
                priority: form.priority,
                startAt: new Date(form.startAt).toISOString(),
                durationMinutes: parseInt(form.durationMinutes, 10) || 15,
                reminderMinutesBefore: parseInt(form.reminderMinutesBefore, 10) || 0,
                notes: form.notes,
                ...(lead?._id && !isEdit ? { leadId: lead._id } : {})
            };

            let res;
            if (isEdit) {
                res = await updateCalendarEvent(existingEvent._id, payload);
            } else {
                res = await createCalendarEvent(payload);
            }
            toast.success(isEdit ? "Callback updated" : "Callback scheduled");
            onSaved?.(res?.data);
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save callback");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="max-w-md rounded-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        {isEdit ? "Edit Event" : "Schedule Callback"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-gray-500">
                        {lead
                            ? <>For lead: <span className="font-medium text-gray-700">{lead.firstName || ""} {lead.lastName || ""}</span>{lead.phone && <> · <Phone className="inline h-3 w-3 mx-0.5 align-baseline" />{lead.phone}</>}</>
                            : "Schedule a callback, follow-up or task on your calendar."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-medium text-gray-700">Title *</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="e.g. Callback re: pricing"
                            disabled={saving}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-medium text-gray-700">Type</Label>
                            <Select
                                options={[
                                    { label: "📞 Callback", value: "callback" },
                                    { label: "📅 Follow-up", value: "follow_up" },
                                    { label: "💼 Meeting", value: "meeting" },
                                    { label: "✅ Task", value: "task" }
                                ]}
                                value={form.type}
                                onChange={(value) => setForm({ ...form, type: value })}
                                disabled={saving}
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-gray-700">Priority</Label>
                            <Select
                                options={[
                                    { label: "Low", value: "low" },
                                    { label: "Normal", value: "normal" },
                                    { label: "High", value: "high" },
                                    { label: "Urgent", value: "urgent" }
                                ]}
                                value={form.priority}
                                onChange={(value) => setForm({ ...form, priority: value })}
                                disabled={saving}
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-medium text-gray-700">Date & Time *</Label>
                        <Input
                            type="datetime-local"
                            value={form.startAt}
                            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
                            disabled={saving}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-medium text-gray-700">Duration (min)</Label>
                            <Input
                                type="number"
                                min="1"
                                value={form.durationMinutes}
                                onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
                                disabled={saving}
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-gray-700">Remind me</Label>
                            <Select
                                options={[
                                    { label: "No reminder", value: "0" },
                                    { label: "5 min before", value: "5" },
                                    { label: "10 min before", value: "10" },
                                    { label: "15 min before", value: "15" },
                                    { label: "30 min before", value: "30" },
                                    { label: "1 hour before", value: "60" },
                                    { label: "2 hours before", value: "120" },
                                    { label: "1 day before", value: "1440" }
                                ]}
                                value={String(form.reminderMinutesBefore)}
                                onChange={(value) => setForm({ ...form, reminderMinutesBefore: value })}
                                disabled={saving}
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-medium text-gray-700">Notes (optional)</Label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            placeholder="Why are you calling? What to discuss?"
                            className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            disabled={saving}
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            {isEdit ? "Save Changes" : "Schedule"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Convert any Date/ISO string into the value format <input type="datetime-local"> expects (local timezone, no Z).
function localInputValue(input) {
    const d = input instanceof Date ? input : new Date(input);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
